import { alpha, makeStyles } from '@material-ui/core';
import * as React from 'react';
import { useEffect } from 'react';
import { AutocompleteInput, Filter, ReferenceInput, TextInput, useListContext, useTranslate } from 'react-admin';
import { GROUP_METADATA_AVATAR_TYPES, METADATA_AVATAR_TYPES } from '../../../providers/resources';
import StatusFilter from './StatusFilter';
import DepartmentFilterInput from './DepartmentFilterInput';
import { getNameWithoutPrefixFromJson as getGroupMetadataAvatarTypeNameWithoutPrefixFromJson } from '../../../utils/resources/GroupMetadataAvatarType';
import { getNameWithoutPrefixFromJson } from '../../../utils/resources/MetadataAvatarType';
import PeriodInput from './PeriodInput';
import PeriodInputInstallationDate from './PeriodInputInstallationDate';

const styles = (theme: any) => ({
    filterContainer: {
        backgroundColor: 'white',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '16px',
    },
    actionBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        gap: '12px',
    },
    actionButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    actionButton: {
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.5px',
        padding: '8px 16px',
        borderRadius: '6px',
        minHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    addFilterButton: {
        color: theme.palette.primary.main,
        backgroundColor: 'transparent',
        border: 'none',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
    },
    customizeButton: {
        color: theme.palette.primary.main,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
    },
    refreshButton: {
        color: 'white',
        backgroundColor: theme.palette.primary.main,
        border: 'none',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    divider: {
        margin: '0 20px',
        backgroundColor: theme.palette.grey[300],
    },
    form: {
        marginLeft: 0,
        padding: '20px 24px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '16px',
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '20px',
        alignItems: 'flex-end',
    },
    inputField: {
        minWidth: '200px',
        '& .MuiInputLabel-root': {
            fontSize: '0.75rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            letterSpacing: '0.5px',
            marginBottom: '4px',
        },
        '& .MuiInput-underline:before': {
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
        '& .MuiInput-underline:after': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
        '& .MuiInputBase-input': {
            fontSize: '0.875rem',
            padding: '8px 0',
        },
        '& .MuiAutocomplete-inputRoot': {
            fontSize: '0.875rem',
        },
    },
    dateField: {
        minWidth: '180px',
        '& .MuiInputLabel-root': {
            fontSize: '0.75rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            letterSpacing: '0.5px',
            marginBottom: '4px',
        },
        '& .MuiInput-underline:before': {
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
        '& .MuiInput-underline:after': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
        '& .MuiInputBase-input': {
            fontSize: '0.875rem',
            padding: '8px 0',
        },
    },
    periodField: {
        minWidth: '300px',
        '& .MuiInputLabel-root': {
            fontSize: '0.75rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            letterSpacing: '0.5px',
            marginBottom: '4px',
        },
    },
});
const useStyles = makeStyles(styles);

const SimpleListFilter = (props: any) => {
    const t = useTranslate();
    const classes = useStyles();

    const { setFilters, displayedFilters, filterValues } = useListContext();

    useEffect(() => {
        if (!('note_v0' in displayedFilters)) {
            let newFilters = { ...filterValues };
            delete newFilters['note_v0__createdAt_after'];
            delete newFilters['note_v0__createdAt_before'];
            delete newFilters['note_v0__tags'];
            setFilters(newFilters, displayedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedFilters]);

    return (
        <Filter
            {...props}
            classes={{
                form: classes.form,
            }}
        >
            <StatusFilter
                // @ts-ignore
                className='firstInput'
                alwaysOn={true}
                source='currentNode.name'
                label={t('app.ancs.nodeStateName')}
            />

            <DepartmentFilterInput source='department' alwaysOn={true} />

            <TextInput
                size={'small'}
                variant={'standard'}
                label={t('app.ancs.serialNumber')}
                source='palphaId'
                alwaysOn={false}
                InputLabelProps={{ shrink: true }}
                // classes={{ root: classes.inputField }}
            />

            <PeriodInputInstallationDate source='productInstallationDate' label={t('app.ancs.installingDate')} />

            <ReferenceInput
                size={'medium'}
                variant={'standard'}
                label={t('app.ancs.name')}
                source='metadataAvatarType.id'
                reference={METADATA_AVATAR_TYPES}
                alwaysOn={false}
                filterToQuery={(searchText) => ({ name: searchText })}
                // classes={{ root: classes.inputField }}
            >
                <AutocompleteInput size={'medium'} variant={'standard'} optionText={getNameWithoutPrefixFromJson} />
            </ReferenceInput>

            <ReferenceInput
                size={'medium'}
                variant={'standard'}
                label={t('app.ancs.productTypology')}
                source='metadataAvatarType.groupMetadataAvatarType.id'
                reference={GROUP_METADATA_AVATAR_TYPES}
                alwaysOn={false}
                filterToQuery={(searchText) => ({ name: searchText })}
                // classes={{ root: classes.inputField }}
            >
                <AutocompleteInput
                    size={'medium'}
                    variant={'standard'}
                    optionText={getGroupMetadataAvatarTypeNameWithoutPrefixFromJson}
                />
            </ReferenceInput>

            <PeriodInput
                source='note_v0'
                label={t('app.ancs.sludgeEmptyingThresholdFilterDate')}
                // classes={{ root: classes.periodField }}
            />
            {/*<RadioButtonGroupInput*/}
            {/*    helperText={false}*/}
            {/*    label={"app.traceabilityNotesThreshold.name"}*/}
            {/*    source={encodeURI(`${props.source}[customFieldsThresholdName]`)}*/}
            {/*    choices={thresholdsNameChoices(t)}*/}
            {/*/>*/}

            {/*<ThresholdNameSelectInput*/}
            {/*    source={encodeURI(`${props.source}[customFieldsThresholdName]`)}*/}
            {/*    label={t('app.traceabilityNotesThreshold.name')}*/}
            {/*/>*/}
            {/*{userConnected && (*/}
            {/*    <ReferenceInput*/}
            {/*        filter={{*/}
            {/*            isDeployed: true,*/}
            {/*            companyId: userConnected?.company.id,*/}
            {/*        }}*/}
            {/*        size={'medium'}*/}
            {/*        variant={'standard'}*/}
            {/*        label={t('app.ancs.nodeStateName')}*/}
            {/*        source='currentNode.name'*/}
            {/*        reference={RUNNER_TEMPLATES}*/}
            {/*        alwaysOn={true}*/}
            {/*    >*/}
            {/*        <CustomAutocompleteInputCurrentNode id={'customAutocompleteInputCurrentNode'} />*/}
            {/*    </ReferenceInput>*/}
            {/*)}*/}
        </Filter>
    );
};
export default SimpleListFilter;
